/**
 * referralLetterPDF.js — v240.0.2 (REF-O)
 *
 * Renders the specialist referral letter for the PATIENT from data already on
 * their case (the MD auto-drafts it into diagnose.referralLetter when a referral
 * order is added). Nothing is fetched or "tracked over" — the letter content is
 * already here on the originating case. Uses the same window.open + print
 * approach as MyMedicalNote.jsx / visitPrepPDF.js, and mirrors the MD-side
 * generateReferralLetterHTML layout so the patient's copy reads identically.
 */

const NAVY = '#15354F';
const GREY = '#64748B';

const esc = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const paras = (txt) =>
  esc(txt)
    .split(/\n{2,}|\r\n\r\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p style="margin:0 0 12px;">${p.replace(/\n/g, '<br>')}</p>`)
    .join('');

import { Capacitor } from '@capacitor/core';
import { sharePdfFromHtml } from './sharePdf';
import { formatSex } from './formatSex'
import { isOutsideAppointment } from './outsideAppointment'

function fmtAddr(a) {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return [a.street || a.line1, a.city, a.state, a.zip].filter(Boolean).join(', ');
}

/** Compose the referral body text from whatever shape diagnose.referralLetter is. */
function resolveContent(currentCase, referral) {
  const rl = currentCase?.diagnose?.referralLetter;
  let reason = '';
  let consultantQuestion = '';
  if (rl && typeof rl === 'object') {
    reason = [
      rl.reasonForReferral,
      rl.clinicalSummaryNarrative,
      rl.priorTreatmentsSummary,
      rl.urgencyJustification,
    ]
      .filter(Boolean)
      .join('\n\n');
    const q = rl.specificQuestions;
    consultantQuestion = Array.isArray(q) ? q.filter(Boolean).join('\n') : q || '';
  } else if (typeof rl === 'string') {
    reason = rl;
  }
  // Fallbacks from the referral record itself.
  if (!reason) reason = referral?.reason || referral?.clinical_reason || referral?.clinicalReason || '';
  if (!consultantQuestion) consultantQuestion = referral?.consultant_question || referral?.consultantQuestion || '';
  return { reason, consultantQuestion };
}

/**
 * openReferralLetter — builds the letter HTML and opens a print-ready window.
 * @returns {boolean} false if there was nothing to render.
 */
export function openReferralLetter({ currentCase, profile, referral } = {}) {
  if (!currentCase) return false;

  const { reason, consultantQuestion } = resolveContent(currentCase, referral);

  // Doctor (referring) — pull whatever the patient case carries; degrade gracefully.
  const doc = currentCase?.doctor || {};
  const apt = (currentCase?.appointments || []).find((a) => !isOutsideAppointment(a) && (a?.doctor_name || a?.doctor_first_name)) || {};
  const doctorName =
    doc.name ||
    [doc.first_name || doc.firstName, doc.last_name || doc.lastName].filter(Boolean).join(' ').trim() ||
    apt.doctor_name ||
    referral?.referring_doctor ||
    referral?.referringDoctor ||
    'Referring Physician';
  const credentials = doc.credentials || 'MD';
  const practiceName = doc.practice_name || doc.practiceName || '';
  const practiceAddress = fmtAddr(doc.practice_address || doc.practiceAddress);
  const practicePhone = doc.practice_phone || doc.practicePhone || '';
  const npi = doc.npi || '';

  // Patient identity.
  const id = profile?.identification || {};
  const u = currentCase?.users || {};
  const patientName =
    [id.firstName || u.first_name, id.lastName || u.last_name].filter(Boolean).join(' ').trim() || 'the patient';
  const dob = id.dob || id.dateOfBirth || '';
  const sex = formatSex(id.sex || id.gender);   // SEX-CAPITALISE-SWEEP-2
  const idLine = [dob ? `DOB ${dob}` : '', sex].filter(Boolean).join(' · ');

  const specialty = referral?.specialty || currentCase?.specialty || 'Specialist';
  const focusArea = referral?.focus_area || referral?.focusArea || currentCase?.focus_area || '';
  const referredFor = [specialty, focusArea].filter(Boolean).join(' — ');

  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const lhBlock = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${NAVY};padding-bottom:12px;margin-bottom:18px;">
      <div>
        <div style="font-size:15pt;font-weight:700;color:${NAVY};">${esc(doctorName)}, ${esc(credentials)}</div>
        ${practiceName ? `<div style="font-size:10.5pt;color:#475569;">${esc(practiceName)}</div>` : ''}
        ${practiceAddress ? `<div style="font-size:10pt;color:${GREY};">${esc(practiceAddress)}</div>` : ''}
        <div style="font-size:10pt;color:${GREY};">${practicePhone ? esc(practicePhone) : ''}${
          npi ? `${practicePhone ? ' · ' : ''}NPI ${esc(npi)}` : ''
        }</div>
      </div>
      <div style="font-size:11pt;font-weight:700;letter-spacing:1.5px;color:${NAVY};">ORELLA</div>
    </div>`;

  const body = `
    ${lhBlock}
    <div style="font-size:10.5pt;color:${GREY};margin-bottom:18px;">${esc(dateStr)}</div>
    <div style="background:#F8FAFB;border:1px solid #E8F1F8;border-radius:8px;padding:12px 16px;margin-bottom:18px;">
      <div style="font-size:9pt;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:#94A3B8;margin-bottom:4px;">Re: ${esc(patientName)}</div>
      ${idLine ? `<div style="font-size:10.5pt;color:${NAVY};">${esc(idLine)}</div>` : ''}
      ${referredFor ? `<div style="font-size:10.5pt;color:${NAVY};margin-top:2px;"><strong>Referred for:</strong> ${esc(referredFor)}</div>` : ''}
    </div>
    <p style="margin:0 0 12px;">Dear Colleague,</p>
    ${reason && reason.trim() ? paras(reason) : `<p style="margin:0 0 12px;">I am referring ${esc(patientName)} for your evaluation and management.</p>`}
    ${
      consultantQuestion && consultantQuestion.trim()
        ? `<div style="margin:14px 0;">
            <div style="font-size:9pt;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:${NAVY};margin-bottom:4px;">Specific question for consultant</div>
            ${paras(consultantQuestion)}
          </div>`
        : ''
    }
    <p style="margin:14px 0 0;">Relevant clinical history, medications, and allergies are enclosed with this referral. Thank you for seeing this patient.</p>
    <div style="margin-top:28px;">
      <div>Sincerely,</div>
      <div style="margin-top:18px;font-weight:600;color:${NAVY};">${esc(doctorName)}, ${esc(credentials)}</div>
      ${practiceName ? `<div style="font-size:10pt;color:${GREY};">${esc(practiceName)}</div>` : ''}
    </div>`;

  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8" />
<title>Referral Letter — ${esc(patientName)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; box-sizing:border-box; }
  body { font-family:'Inter',system-ui,sans-serif; color:#1E293B; margin:0; padding:48px 56px; line-height:1.6; font-size:11pt; }
  @page { margin:18mm; }
  .footer { margin-top:36px; padding-top:12px; border-top:1px solid #E2E8F0; font-size:8.5pt; color:#94A3B8; text-align:center; }
</style></head>
<body>
  ${body}
  <div class="footer">Confidential Medical Information · Generated via Orella Health</div>
</body></html>`;

  if (Capacitor.isNativePlatform()) {
    sharePdfFromHtml(html, 'Referral Letter');
    return true;
  }
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch (e) {
      /* user can print manually */
    }
  }, 600);
  return true;
}

export default openReferralLetter;
